import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useDispatch } from "react-redux";
import { addNotification } from "../store/slices/notificationSlice";
import {
  Form,
  Input,
  Button,
  Typography,
  Card,
  Row,
  Col,
  Select,
  Divider,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import "./UserForm.scss";
import ManageTeam from "./ManageTeam";

const { Title } = Typography;
const { Option } = Select;

interface UserFormData {
  username: string;
  password: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;
}

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const isEditMode = !!userId;
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [team, setTeam] = useState([]);
  const [manager, setManager] = useState<any>(null);

  useEffect(() => {
    if (isEditMode) {
      fetchUserData();
    }
  }, [userId]);

  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`/users/${userId}`);
      form.setFieldsValue({
        ...data,
        password: "", // Don't show password in edit mode
      });
      setManager(data.manager);
      setTeam(data.immediateDependants || []);
    } catch (error) {
      console.error("Error fetching user:", error);
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to load user data",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onFinish = async (values: UserFormData) => {
    setIsLoading(true);
    try {
      if (isEditMode) {
        await axios.put(`/users/${userId}`, values);
        dispatch(
          addNotification({
            type: "success",
            message: "User updated successfully!",
          })
        );
      } else {
        await axios.post("/users", values);
        dispatch(
          addNotification({
            type: "success",
            message: "User created successfully!",
          })
        );
      }
      navigate("/users");
    } catch (error) {
      console.error("Error saving user:", error);
      dispatch(
        addNotification({
          type: "error",
          message: "Failed to save user data",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="user-form-container">
      <div className="sticky-header">
        <Title level={2} className="form-title">
          {isEditMode ? "Edit User" : "Create New User"}
        </Title>
      </div>
      <div className="user-form-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: "Please enter username" }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter username"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={
              isEditMode ? "Password (leave blank to keep current)" : "Password"
            }
            rules={[
              { required: !isEditMode, message: "Please enter password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter password"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter email"
              size="large"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: "Please enter first name" }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Enter first name"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: "Please enter last name" }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Enter last name"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit phone number",
              },
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Enter phone number"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="panCard"
            label="PAN Card Number"
            rules={[
              {
                required: true,
                message: "Please enter your PAN card number",
              },
              {
                pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                message: "Please enter a valid PAN card number",
              },
            ]}
          >
            <Input
              prefix={<IdcardOutlined />}
              placeholder="Enter PAN card number"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please select a role" }]}
          >
            <Select
              placeholder="Select a role"
              size="large"
              prefix={<TeamOutlined />}
            >
              <Option value="MANAGER">Manager</Option>
              <Option value="USER">User</Option>
            </Select>
          </Form.Item>

          <Form.Item className="form-actions">
            <Button
              type="default"
              onClick={() => navigate("/users")}
              size="large"
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="submit-button"
              loading={isLoading}
            >
              {isLoading
                ? "Saving..."
                : isEditMode
                ? "Update User"
                : "Create User"}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div className="manage-team">
        <>
          <Divider />
          <div>
            <b>Manager:</b>{" "}
            {manager ? (
              <>
                {" "}
                {manager.firstName} {manager.lastName} -{" "}
                <i>{manager.username}</i>
              </>
            ) : (
              <>No Manager</>
            )}
          </div>
          <Divider />
        </>
        <ManageTeam userId={userId} data={team} />
      </div>
    </div>
  );
};

export default UserForm;
